import { useState, useEffect, useCallback } from 'react'
import { Sparkles, ExternalLink, Loader, Calendar, X, CheckCircle2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { track } from '../../lib/analytics'
import { PLANS, SESSION_PLAN_IDS, SESSION_FEATURES, formatUSD, formatCOP } from '../../constants/pricing'

/**
 * SessionBookingCard
 * - Sells packs of consultant sessions through MercadoPago.
 * - Once a user has credits, exposes an inline Calendly widget that books a
 *   real meeting and atomically deducts one credit on `event_scheduled`.
 *
 * Calendly is embedded as an iframe so we can listen to its postMessage events
 * (https://help.calendly.com/hc/en-us/articles/223147027) without needing to
 * load their JS SDK. When `calendly.event_scheduled` fires we record a
 * `session_bookings` row and call the `consume_session_credit` RPC.
 *
 * Without `VITE_CALENDLY_URL`, the booking button degrades to a mailto so the
 * feature still works before Calendly is configured.
 */
export default function SessionBookingCard({ creditsRemaining = 0, consultancyId }) {
  const [loadingPlan, setLoadingPlan] = useState(null)
  const [error, setError]             = useState('')
  const [showCalendly, setShowCalendly] = useState(false)
  const [bookingDone, setBookingDone] = useState(false)

  const calendlyUrl = import.meta.env.VITE_CALENDLY_URL
  const fallbackMailto = 'mailto:estrategia@nextvalleyco.com?subject=Quiero agendar mi sesión con un consultor NVC'

  async function handleBuy(planId) {
    setLoadingPlan(planId)
    setError('')
    track('caja_session_pack_clicked', { plan_id: planId })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-mp-preference`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ planId, consultancy_id: consultancyId }),
        }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Error al crear el pago')
      window.location.href = import.meta.env.DEV ? data.sandbox_init_point : data.init_point
    } catch (err) {
      setError(err.message ?? 'Ocurrió un error. Intenta de nuevo.')
      setLoadingPlan(null)
    }
  }

  function openBooking() {
    track('caja_session_book_clicked', { credits_remaining: creditsRemaining })
    if (calendlyUrl) {
      setShowCalendly(true)
    } else {
      window.open(fallbackMailto, '_blank')
    }
  }

  // ── Calendly postMessage handler ───────────────────────────────────────────
  const handleCalendlyMessage = useCallback(async (e) => {
    if (typeof e.data !== 'object' || !e.data?.event) return
    if (!String(e.data.event).startsWith('calendly.')) return

    if (e.data.event === 'calendly.event_scheduled') {
      const eventUri = e.data.payload?.event?.uri ?? null
      try {
        // Record the booking
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          await supabase.from('session_bookings').insert({
            user_id:        user.id,
            consultancy_id: consultancyId ?? null,
            status:         'confirmed',
            external_event_id: eventUri,
            scheduled_at:   e.data.payload?.event?.start_time ?? null,
          })
          // Atomically deduct one credit
          await supabase.rpc('consume_session_credit', { p_user_id: user.id })
        }
        setBookingDone(true)
        track('caja_session_booked', { external_event_id: eventUri })
      } catch (err) {
        console.warn('[SessionBookingCard] booking record failed:', err)
        // Still show success — the meeting IS booked in Calendly even if we
        // couldn't record it on our side. We'll reconcile via Calendly webhook.
        setBookingDone(true)
      }
    }
  }, [consultancyId])

  useEffect(() => {
    if (!showCalendly) return
    window.addEventListener('message', handleCalendlyMessage)
    return () => window.removeEventListener('message', handleCalendlyMessage)
  }, [showCalendly, handleCalendlyMessage])

  return (
    <>
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={16} className="text-brand-700" />
              <h3 className="text-base font-bold text-gray-900">Sesiones con un consultor NVC</h3>
            </div>
            <p className="text-sm text-gray-500 max-w-md leading-relaxed">
              Compra un pack de sesiones para que un consultor revise tu plan, te dé feedback estratégico y resuelva preguntas concretas.
            </p>
          </div>
          {creditsRemaining > 0 && (
            <button
              onClick={openBooking}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap flex-shrink-0"
            >
              <Calendar size={14} /> Agendar sesión ({creditsRemaining} disponible{creditsRemaining === 1 ? '' : 's'})
            </button>
          )}
        </div>

        {creditsRemaining === 0 && (
          <ul className="text-xs text-gray-500 space-y-1 mb-4">
            {SESSION_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-brand-600 flex-shrink-0">·</span>{f}
              </li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SESSION_PLAN_IDS.map(id => {
            const plan = PLANS[id]
            const perSession = Math.round(plan.priceUSD / plan.sessions)
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleBuy(id)}
                disabled={loadingPlan !== null}
                className="relative text-left rounded-xl border-2 border-gray-200 hover:border-brand-600 hover:bg-brand-50 p-4 transition-all disabled:opacity-60"
              >
                {plan.badge && (
                  <span className="absolute -top-2 right-3 text-[10px] font-bold uppercase tracking-wide bg-brand-700 text-white px-2 py-0.5 rounded-full">
                    {plan.badge}
                  </span>
                )}
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{plan.sessions} sesion{plan.sessions === 1 ? '' : 'es'}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatUSD(plan.priceUSD)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">≈ {formatCOP(plan.priceCOP)}</p>
                {plan.sessions > 1 && (
                  <p className="text-[11px] text-emerald-600 font-semibold mt-1">USD ${perSession}/sesión</p>
                )}
                <div className="mt-2 text-xs font-semibold text-brand-700 flex items-center gap-1">
                  {loadingPlan === id ? <Loader size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                  {loadingPlan === id ? 'Redirigiendo…' : 'Comprar'}
                </div>
              </button>
            )
          })}
        </div>

        {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
      </div>

      {/* ── Calendly modal ── */}
      {showCalendly && (
        <CalendlyModal
          url={calendlyUrl}
          done={bookingDone}
          onClose={() => { setShowCalendly(false); setBookingDone(false) }}
        />
      )}
    </>
  )
}

function CalendlyModal({ url, done, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-brand-700" />
            <h2 className="text-sm font-bold text-gray-900">Agendar sesión con consultor</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="flex-1 flex items-center justify-center p-10 text-center">
            <div>
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={26} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">¡Sesión agendada!</h3>
              <p className="text-sm text-gray-500 max-w-xs">
                Recibirás un correo de confirmación con el link de la videollamada. Te restamos un crédito de tu pack.
              </p>
              <button
                onClick={onClose}
                className="mt-5 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                Volver al dashboard
              </button>
            </div>
          </div>
        ) : (
          <iframe
            src={`${url}?embed_domain=${window.location.hostname}&embed_type=Inline&hide_landing_page_details=1`}
            title="Agenda tu sesión NVC"
            className="flex-1 w-full"
            style={{ minHeight: 600, border: 0 }}
          />
        )}
      </div>
    </div>
  )
}
